import { format, parseISO } from 'date-fns';
import type { AnyJourneyItemTransformed, LegTransformed, StayTransformed } from '~/types/JourneyItem.types';
import type { ItineraryGroup } from './TripItinerary.types';

type RawGroup = {
  startDate: string;
  endDate: string;
  items: AnyJourneyItemTransformed[];
};

function mergeItemIntoGroup(group: RawGroup, item: AnyJourneyItemTransformed) {
  group.items.push(item);
  if (item.endDate > group.endDate) group.endDate = item.endDate;
  if (item.startDate < group.startDate) group.startDate = item.startDate;
}

function startGroup(item: AnyJourneyItemTransformed): RawGroup {
  return { startDate: item.startDate, endDate: item.endDate, items: [item] };
}

/**
 * Groups each stay with all journey legs immediately before it (e.g. travel to Turin + 3 nights
 * in Turin → one timeline group spanning Day 1–4). Trailing legs without a following stay (e.g.
 * return journey) form their own group.
 */
function groupByTravelAndStay(items: AnyJourneyItemTransformed[]): RawGroup[] {
  const groups: RawGroup[] = [];
  let pendingJourneys: RawGroup | null = null;

  for (const item of items) {
    if (item.type === 'Stay') {
      if (pendingJourneys) {
        mergeItemIntoGroup(pendingJourneys, item);
        groups.push(pendingJourneys);
        pendingJourneys = null;
      } else {
        groups.push(startGroup(item));
      }
      continue;
    }

    if (item.type === 'Journey') {
      if (pendingJourneys) {
        mergeItemIntoGroup(pendingJourneys, item);
      } else {
        pendingJourneys = startGroup(item);
      }
    }
  }

  if (pendingJourneys) {
    groups.push(pendingJourneys);
  }

  return groups;
}

/**
 * Groups items by start date, keyed so multi-day journeys land in the group they arrive into.
 * Used for the accommodation stays view, which only ever receives Stay items.
 */
function groupByStartDate(items: AnyJourneyItemTransformed[]): RawGroup[] {
  const groupMap = new Map<string, RawGroup>();

  for (const item of items) {
    const key = item.type === 'Journey' && item.startDate !== item.endDate ? item.endDate : item.startDate;
    const existing = groupMap.get(key);

    if (existing) {
      existing.items.push(item);
      if (item.endDate > existing.endDate) existing.endDate = item.endDate;
      if (item.startDate < existing.startDate) existing.startDate = item.startDate;
    } else {
      groupMap.set(key, { startDate: item.startDate, endDate: item.endDate, items: [item] });
    }
  }

  return Array.from(groupMap.values());
}

function computeDayNumber(groups: RawGroup[], targetIndex: number): number {
  let day = 1;
  for (let i = 0; i < targetIndex; i++) {
    const start = parseISO(groups[i].startDate);
    const end = parseISO(groups[i].endDate);
    day += Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000));
  }
  return day;
}

function computeDayNumberFromTripStart(tripStartDate: string, groupStartDate: string): number {
  const diff = Math.round((parseISO(groupStartDate).getTime() - parseISO(tripStartDate).getTime()) / 86_400_000);
  return Math.max(1, diff + 1);
}

function formatDayLabel(group: RawGroup, startDay: number): string {
  const start = parseISO(group.startDate);
  const end = parseISO(group.endDate);
  const span = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);

  if (span <= 1) return `Day ${startDay}`;
  return `Day ${startDay} - ${startDay + span - 1}`;
}

function formatDateRange(group: RawGroup): string {
  const start = parseISO(group.startDate);
  const end = parseISO(group.endDate);

  if (group.startDate === group.endDate) {
    return format(start, 'd MMM yy');
  }

  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    return `${format(start, 'd')} - ${format(end, 'd MMM yy')}`;
  }

  return `${format(start, 'd MMM yy')} - ${format(end, 'd MMM yy')}`;
}

function enrichGroup(
  group: RawGroup,
  allGroups: RawGroup[],
  indexInAll: number,
  tripStartDate?: string
): ItineraryGroup {
  const startDay = tripStartDate
    ? computeDayNumberFromTripStart(tripStartDate, group.startDate)
    : computeDayNumber(allGroups, indexInAll);
  return {
    id: `${group.startDate}-${group.endDate}`,
    startDate: group.startDate,
    endDate: group.endDate,
    dayLabel: formatDayLabel(group, startDay),
    startDay,
    dateRange: formatDateRange(group),
    items: group.items
  };
}

export function getItemDayNumber(group: ItineraryGroup, itemStartDate: string): number {
  const offset = Math.round((parseISO(itemStartDate).getTime() - parseISO(group.startDate).getTime()) / 86_400_000);
  return group.startDay + offset;
}

function findReturnLeg(items: AnyJourneyItemTransformed[]): LegTransformed | undefined {
  return [...items].reverse().find((item): item is LegTransformed => item.type === 'Journey');
}

export function computeTotalCarbonFootprint(items: AnyJourneyItemTransformed[]): number {
  return Math.round(items.reduce((acc, item) => acc + (item.carbonFootprint ?? 0), 0));
}

export function getOriginLocationName(items: AnyJourneyItemTransformed[]): string | null {
  const firstLeg = items.find((item): item is LegTransformed => item.type === 'Journey');
  return firstLeg?.origin ?? null;
}

/**
 * Groups itinerary items for the overview timeline: travel into each stay, plus any trailing
 * legs (e.g. return journey). Hero / stay / standard leg cards are chosen at render time.
 */
export function transformItineraryForOverview(items: AnyJourneyItemTransformed[]): {
  groups: ItineraryGroup[];
  originLocationName: string | null;
  returnLegId: string | null;
} {
  const allGroups = groupByTravelAndStay(items);
  const groups = allGroups.map((group, index) => enrichGroup(group, allGroups, index));
  const returnLeg = findReturnLeg(items);

  return {
    groups,
    originLocationName: getOriginLocationName(items),
    returnLegId: returnLeg?.id ?? null
  };
}

export function transformItineraryForTransportLegs(items: AnyJourneyItemTransformed[]): {
  groups: ItineraryGroup[];
  returnLegId: string | null;
} {
  const legs = items.filter((item): item is LegTransformed => item.type === 'Journey');
  const allGroups = groupByStartDate(legs);
  const returnLeg = findReturnLeg(legs);
  const tripStartDate = items.reduce<string | undefined>(
    (earliest, item) => (earliest === undefined || item.startDate < earliest ? item.startDate : earliest),
    undefined
  );

  return {
    groups: allGroups.map((group, index) => enrichGroup(group, allGroups, index, tripStartDate)),
    returnLegId: returnLeg?.id ?? null
  };
}

export function transformItineraryForAccommodationStays(items: AnyJourneyItemTransformed[]): {
  groups: ItineraryGroup[];
} {
  const stays = items.filter((item): item is StayTransformed => item.type === 'Stay');
  const allGroups = groupByStartDate(stays);

  return {
    groups: allGroups.map((group, index) => enrichGroup(group, allGroups, index))
  };
}
